import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../../api";
import { SortableTable } from "../../components/SortableTable";
import { timeAgo } from "../../format";
import { Card, Stat } from "../../shell/ui";

/** The admin tabs that watch the platform live: activity and the simulator (the fleet is in Bots.tsx). */

interface Hour {
  /** The hour in UTC ("HH:00"); `at` is the same hour as a timestamp. */
  hour: string;
  at: string;
  runs: number;
  comments: number;
  ratings: number;
  trades: number;
  signins: number;
}

export function ActivityTab() {
  const q = useQuery({
    queryKey: ["admin", "activity"],
    queryFn: () => api<{ items: Hour[] }>("/admin/activity"),
    refetchInterval: 60_000,
  });
  const rows = q.data?.items ?? [];
  const sum = (k: keyof Hour) => rows.reduce((s, r) => s + Number(r[k]), 0);
  const max = Math.max(1, ...rows.map((r) => r.runs + r.comments + r.ratings + r.trades));
  return (
    <div className="stack">
      <Card title="Last 24 hours">
        <div className="stat-grid stat-grid--pairs">
          <Stat label="Backtests" value={sum("runs")} />
          <Stat label="Comments" value={sum("comments")} />
          <Stat label="Ratings" value={sum("ratings")} />
          <Stat label="Bot trades" value={sum("trades")} />
          <Stat label="Sign-ins" value={sum("signins")} />
        </div>
      </Card>
      <Card title="Per hour" flush>
        <SortableTable
          exportName="ops-per-hour"
          rows={rows}
          loading={q.isPending}
          rowKey={(r) => r.hour}
          columns={[
            {
              key: "hour",
              label: "Hour",
              // Local time, like the audit log next to it; `hour` is UTC.
              render: (r) => (
                <span className="mono">
                  {new Date(r.at).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hourCycle: "h23",
                  })}
                </span>
              ),
              sortValue: (r) => rows.indexOf(r),
              firstDirection: "desc",
            },
            {
              key: "load",
              label: "Activity",
              render: (r) => (
                <span className="bars__track" style={{ display: "block", minWidth: "8rem" }}>
                  <span
                    className="bars__fill"
                    style={{
                      width: `${((r.runs + r.comments + r.ratings + r.trades) / max) * 100}%`,
                    }}
                  />
                </span>
              ),
              sortValue: (r) => r.runs + r.comments + r.ratings + r.trades,
            },
            {
              key: "runs",
              label: "Backtests",
              align: "right",
              render: (r) => r.runs,
              sortValue: (r) => r.runs,
            },
            {
              key: "comments",
              label: "Comments",
              align: "right",
              render: (r) => r.comments,
              sortValue: (r) => r.comments,
            },
            {
              key: "ratings",
              label: "Ratings",
              align: "right",
              render: (r) => r.ratings,
              sortValue: (r) => r.ratings,
            },
            {
              key: "trades",
              label: "Bot trades",
              align: "right",
              render: (r) => r.trades,
              sortValue: (r) => r.trades,
            },
            {
              key: "signins",
              label: "Sign-ins",
              align: "right",
              render: (r) => r.signins,
              sortValue: (r) => r.signins,
            },
          ]}
        />
      </Card>
    </div>
  );
}

interface SimResponse {
  byStatus: Array<{ status: string; n: number }>;
  byKind: Array<{ kind: string; done: number; skipped: number; failed: number; planned: number }>;
  recent: Array<{
    id: number;
    kind: string;
    status: string;
    scheduled_at: string;
    error: string | null;
    username: string;
  }>;
  residents: Array<{
    username: string;
    display_name: string;
    role: string;
    last_seen_at: string | null;
    strategies: number;
    bots: number;
    comments: number;
  }>;
}

export function SimTab() {
  const q = useQuery({
    queryKey: ["admin", "sim"],
    queryFn: () => api<SimResponse>("/admin/sim"),
    refetchInterval: 30_000,
  });
  const n = (s: string) => q.data?.byStatus.find((x) => x.status === s)?.n ?? 0;
  return (
    <div className="stack">
      <p className="muted">
        The simulated residents (hub-sim) act through the public API like any member, badged as simulated
        everywhere. Their numbers are real backtests and real paper bots.
      </p>
      <Card title="Simulator, last 24 hours">
        <div className="stat-grid stat-grid--pairs">
          <Stat label="Done" value={n("done")} />
          <Stat label="Planned" value={n("planned")} />
          <Stat
            label="Skipped"
            value={n("skipped")}
            hint="The API said no (a limit, nothing to act on) — an answer, not a bug"
          />
          <Stat label="Failed" value={n("failed")} hint="Errors — each one is looked at by the audit" />
        </div>
      </Card>
      <Card title="By action" flush>
        <SortableTable
          exportName="residents-by-action"
          rows={q.data?.byKind ?? []}
          loading={q.isPending}
          rowKey={(r) => r.kind}
          initialSort={{ key: "done", direction: "desc" }}
          columns={[
            {
              key: "kind",
              label: "Action",
              render: (r) => r.kind.replace(/_/g, " "),
              sortValue: (r) => r.kind,
              firstDirection: "asc",
            },
            { key: "done", label: "Done", align: "right", render: (r) => r.done, sortValue: (r) => r.done },
            {
              key: "planned",
              label: "Planned",
              align: "right",
              render: (r) => r.planned,
              sortValue: (r) => r.planned,
            },
            {
              key: "skipped",
              label: "Skipped",
              align: "right",
              render: (r) => r.skipped,
              sortValue: (r) => r.skipped,
            },
            {
              key: "failed",
              label: "Failed",
              align: "right",
              render: (r) => <span className={r.failed ? "down" : ""}>{r.failed}</span>,
              sortValue: (r) => r.failed,
            },
          ]}
        />
      </Card>
      <Card title="Residents" flush>
        <SortableTable
          exportName="residents"
          rows={q.data?.residents ?? []}
          loading={q.isPending}
          rowKey={(r) => r.username}
          columns={[
            {
              key: "name",
              label: "Resident",
              render: (r) => <Link to={`/u/${r.username}`}>{r.display_name}</Link>,
              sortValue: (r) => r.display_name,
              firstDirection: "asc",
            },
            {
              key: "role",
              label: "Layout",
              render: (r) => r.role,
              sortValue: (r) => r.role,
              firstDirection: "asc",
            },
            {
              key: "strategies",
              label: "Strategies",
              align: "right",
              render: (r) => r.strategies,
              sortValue: (r) => r.strategies,
            },
            { key: "bots", label: "Bots", align: "right", render: (r) => r.bots, sortValue: (r) => r.bots },
            {
              key: "comments",
              label: "Comments",
              align: "right",
              render: (r) => r.comments,
              sortValue: (r) => r.comments,
            },
            {
              key: "seen",
              label: "Last seen",
              align: "right",
              render: (r) => timeAgo(r.last_seen_at),
              sortValue: (r) => (r.last_seen_at ? new Date(r.last_seen_at).getTime() : null),
            },
          ]}
        />
      </Card>
      <Card title="Latest actions" flush>
        <SortableTable
          exportName="resident-actions"
          rows={q.data?.recent ?? []}
          loading={q.isPending}
          rowKey={(r) => String(r.id)}
          columns={[
            {
              key: "when",
              label: "When",
              render: (r) => timeAgo(r.scheduled_at),
              sortValue: (r) => new Date(r.scheduled_at).getTime(),
            },
            {
              key: "who",
              label: "Resident",
              render: (r) => r.username,
              sortValue: (r) => r.username,
              firstDirection: "asc",
            },
            {
              key: "kind",
              label: "Action",
              render: (r) => r.kind.replace(/_/g, " "),
              sortValue: (r) => r.kind,
              firstDirection: "asc",
            },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <span className={r.status === "failed" ? "down" : r.status === "done" ? "up" : "muted"}>
                  {r.status}
                </span>
              ),
              sortValue: (r) => r.status,
              firstDirection: "asc",
            },
            {
              key: "error",
              label: "Note",
              render: (r) => <span className="muted small">{r.error ?? ""}</span>,
              sortValue: (r) => r.error ?? "",
              firstDirection: "asc",
            },
          ]}
        />
      </Card>
    </div>
  );
}
